import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import OverallSearchPopup from "../../components/OverallSearch/OverallSearchPopup";
import { setWorkflowCustomVariables } from "../../actions/taskSidebar";
import { DESC_TYPE } from "../../components/TaskSidebar/sidebar.constants";
import { number } from "../../config";
import store from "../../store/index";
import { defaultUserChip, editorEntity } from "../editor.Constant";
import { useEditorContext } from "../editor.Context";
import { generateUniqueId } from "../editor.helper";
import BookmarkPopup from "./BookmarkPopup";
import HyperLinkPopup from "./HyperLinkPopup";
import SmartChip from "./SmartChip";
import UpdateChip from "./UpdateChip";
import UpdateIFrame from "./UpdateIFrame";

/**
 * Efficiently splits assignees into recent and other categories
 * @param {Array} recentAssignmentList - List of recent assignments
 * @param {Object} defaultDetails - Default details containing assignee list
 * @returns {Array} Array containing [recentAssignees, otherAssignees]
 * @author Abhishek Dhillon
 */
const splitAssignees = (recentAssignmentList, defaultDetails) => {
  const recent = recentAssignmentList ?? [];
  const assigneeLst = defaultDetails?.assigneeList ?? [];
  const recentIds = new Set(recent.map((a) => a.AssignedUserId));
  return assigneeLst.reduce(
    ([recentAcc, otherAcc], assignee) => {
      (recentIds.has(assignee.value) ? recentAcc : otherAcc).push(assignee);
      return [recentAcc, otherAcc];
    },
    [[], []]
  );
};

/**
 * Popup  for Editor
 * PC KendoEditor
 * @author Shivam Mishra
 */
const EditorPopup = ({
  insertNonEditable,
  updateNonEditable,
  editorRef,
  showEditIframe,
  editableRef,
  acknowledgeMailEditor,
  deleteBookmark,
  taskKey,
  workflowDescriptionProjectId,
}) => {
  const dispatch = useDispatch();
  const editorPopupRef = React.useRef();
  const ref = React.useRef();
  const [assigneeList, setAssigneeList] = useState([]);
  const [isInside, setIsInside] = useState(false);
  const [iframePopupPosition] = useState({
    anchorAlign: { horizontal: "left", vertical: "top" },
    popupAlign: { horizontal: "left", vertical: "bottom" },
  });
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const editorState = useEditorContext();
  const { user } = useSelector((state) => state.auth);
  const { defaultDetails } = useSelector((state) => state.tasks);
  const {
    visible,
    setVisible,
    url,
    selectedLink,
    overallSearchRef,
    taskInfo,
    showInputPopup,
    setInputPopup,
    smartChipTarget,
    editChip,
    showEditChip,
    setShowEditChip,
    smartChipId,
    showTaskSearchPopup,
    setTaskSearchPopup,
    setTempChipId,
    editIframeId,
    hashPopupVisible,
    setHashPopupVisible,
    popupVisible,
    setPopupVisible,
    popupAnchor,
    setPopupAnchor,
    activeTab,
  } = editorState;
  const [popupAlign, setPopupAlign] = useState({ horizontal: "left", vertical: "bottom" });
  const [anchorAlign, setAnchorAlign] = useState({ horizontal: "right", vertical: "center" });
  const [HyperLinkPopupPosition, setHyperLinkPopupPosition] = useState({
    anchorAlign: { horizontal: taskInfo?.Id ? "right" : "center", vertical: "bottom" },
    popupAlign: { horizontal: taskInfo?.Id ? "center" : "left", vertical: "center" },
  });
  const { recentAssignmentList } = useSelector((state) => state.auth);

  const isTaskIdTabActive = showInputPopup && activeTab === "taskId";

  useEffect(() => {
    if ((showInputPopup && smartChipTarget) || (showEditChip && editChip)) {
      const calculatePosition = () => {
        const anchor = showInputPopup ? smartChipTarget : editChip;
        const rect = anchor?.getBoundingClientRect();
        if (rect) {
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const popupHeight = editorPopupRef.current ? editorPopupRef.current.offsetHeight : 0;
          const popupWidth = editorPopupRef.current ? editorPopupRef.current.offsetWidth : 0;
          const spaceBelow = viewportHeight - (rect.top + rect.height);
          const spaceAbove = rect.top;
          const estimatedPopupHeight = popupHeight || number.THREE_HUNDRED; 
          const minimumSpaceRequired = estimatedPopupHeight + number.TWENTY;
          if (spaceBelow < minimumSpaceRequired && spaceAbove >= minimumSpaceRequired) {
            setAnchorAlign({ horizontal: "right", vertical: "top" });
            setPopupAlign({ horizontal: "left", vertical: "bottom" });
          } else {
            setAnchorAlign({ horizontal: "right", vertical: "bottom" });
            setPopupAlign({ horizontal: "left", vertical: "top" });
          }
          setPopupPosition({ top: 0, left: 0 });
        }
      };
      
      calculatePosition();
      
      const timeoutId = setTimeout(() => {
        calculatePosition();
      }, number.FIFTY);
      
      return () => clearTimeout(timeoutId);
    }
  }, [showInputPopup, showEditChip, smartChipTarget, editChip, activeTab]);

  /**
   *useffect to hide a popup.
   * @author Shivam Mishra
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editorPopupRef.current && !editorPopupRef.current.contains(event.target) && event.srcElement.className !== "k-link") {
        /* Don't close if clicking on TimePicker or its popup elements*/
        const isTimePickerElement =
          event.target.closest(".k-timepicker") ||
          event.target.closest(".k-time-list") ||
          event.target.closest(".k-time-container") ||
          event.target.closest(".k-button") ||
          event.target.closest(".k-calendar") ||
          event.target.closest(".k-datepicker-popup") ||
          event.target.closest(".k-dateinput");
        
        if (!isTimePickerElement) {
          hidePopup();
        }
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && (showInputPopup || showEditChip)) {
        hidePopup();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editorPopupRef, showInputPopup, showEditChip]);

  /**
   * useffect to set assignee list from default details.
   */
  useEffect(() => {
    if (defaultDetails?.assigneeList) {
      const [recentAssignees, otherAssignees] = splitAssignees(recentAssignmentList, defaultDetails);
      setAssigneeList([
        defaultUserChip(acknowledgeMailEditor), // Sender Name || Select User
        { label: "Recent", isSubHeading: 1 },
        ...recentAssignees,
        { label: "Others", isSubHeading: 1 },
        ...otherAssignees,
      ]);
    }
  }, [defaultDetails?.assigneeList, showInputPopup, recentAssignmentList]);

  /**
   * calculate HyperLinkPopupPosition based on selectedLink position
   * @author Muskan Thakur
   */
  useEffect(() => {
    if (selectedLink && (hashPopupVisible || visible)) {
      const rect = selectedLink.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const estimatedPopupHeight = number.TWO_HUNDRED;
      const spaceBelow = viewportHeight - (rect.top + rect.height);

      if (spaceBelow < estimatedPopupHeight) {
        setHyperLinkPopupPosition({
          anchorAlign: { horizontal: taskInfo?.Id ? "right" : "center", vertical: "top" },
          popupAlign: { horizontal: taskInfo?.Id ? "left" : "left", vertical: "bottom" },
        });
      } else {
        setHyperLinkPopupPosition({
          anchorAlign: { horizontal: taskInfo?.Id ? "right" : "center", vertical: "bottom" },
          popupAlign: { horizontal: taskInfo?.Id ? "left" : "left", vertical: "center" },
        });
      }
    }
  }, [selectedLink, hashPopupVisible, visible]);

  /**
   *Function to hide a popup.
   * @author Shivam Mishra
   */
  const hidePopup = useCallback(() => {
    if (taskKey === DESC_TYPE.WF_DESCRIPTION) {
      const originals = store.getState()?.taskSidebar?.originalWorkflowVariables;
      dispatch(setWorkflowCustomVariables(Array.isArray(originals) ? [...originals] : []));
    }
    setShowEditChip(false);
    setInputPopup(false);
    setVisible(false);
    setHashPopupVisible(false);
    setTaskSearchPopup(false);
    setTempChipId(null);
    if (editorRef.current && editorRef.current.state && editorRef.current.state.view) {
      const { view } = editorRef.current.state;
      view.focus();
    }
  }, [editorRef, taskKey]);

  /**
   * Effect to ensure OverallSearchPopup is closed when SmartChip popup opens with Task ID tab
   */
  useEffect(() => {
    if (isTaskIdTabActive) {
      setTaskSearchPopup(false);
    }
  }, [isTaskIdTabActive, setTaskSearchPopup]);

  /**
   * Check if the hyperlink popup is still within the KendoEditor viewport
   * @author Muskan Thakur
   */
  const checkPopupVisibility = useCallback(() => {
    if ((hashPopupVisible || visible) && selectedLink && editorRef.current) {
      const editorElement = editorRef.current._contentElement || editorRef.current.dom;
      if (!editorElement) return;

      const editorRect = editorElement.getBoundingClientRect();
      const linkRect = selectedLink.getBoundingClientRect();
      const buffer = number.TEN;

      const isLinkVisible =
        linkRect.top >= editorRect.top - buffer &&
        linkRect.bottom <= editorRect.bottom + buffer &&
        linkRect.left >= editorRect.left - buffer &&
        linkRect.right <= editorRect.right + buffer;

      if (!isLinkVisible) {
        hidePopup();
      }
    }
  }, [hashPopupVisible, visible, selectedLink, editorRef, hidePopup]);

  /**
   * Handle scroll events to check popup visibility
   * @author Muskan Thakur
   */
  useEffect(() => {
    if (!(hashPopupVisible || visible) || !selectedLink) {
      return;
    }

    let timeoutId = null;

    const handleScroll = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        checkPopupVisibility();
      }, number.FIFTEEN);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    if (editorRef.current) {
      const editorElement = editorRef.current._contentElement || editorRef.current.dom;
      if (editorElement) {
        editorElement.addEventListener("scroll", handleScroll, { passive: true });
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("scroll", handleScroll);
      if (editorRef.current) {
        const editorElement = editorRef.current._contentElement || editorRef.current.dom;
        if (editorElement) {
          editorElement.removeEventListener("scroll", handleScroll);
        }
      }
    };
  }, [checkPopupVisibility, editorRef, hashPopupVisible, visible, selectedLink]);

  /**
   * Inserts a task chip into the editor.
   * @param {object} taskDetail - The task object containing Name, Id, and Code.
   * @returns {Promise<void>} A Promise that resolves once the task chip is inserted.
   * @author Shivam Mishra
   */
  const insertTaskChip = async (taskDetail) => {
    //  insertNonEditable
    const { Name, Id, Code } = taskDetail;
    const { view } = editorRef.current.state;
    view.focus();
    const label = `${Code} : ${Name}`;
    const id = await generateUniqueId(user.id, "task", Id);
    updateNonEditable(label, id, "task-chip");
    setTaskSearchPopup(false);
  };

  /**
   *Checks if the mouse cursor is inside the editor's content element.
   *Updates the `isInside` state based on the cursor's position relative to the element's bounding box.
   *@author Shivam Mishra
   */
  const checkIfMouseInside = (event) => {
    const dialogIndexElement = document.getElementsByClassName("dialog-index");
    if (!editorRef.current || dialogIndexElement?.length) return;
    if (document.getElementById("excalidraw-dialog")) return;

    const contentElement = editorRef.current._contentElement;
    const rect = contentElement.getBoundingClientRect();
    const { clientX: mouseX, clientY: mouseY } = event;

    const inside = mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom;

    setIsInside(inside);
  };

  /**
   *useffect to show edit iframe popup when user is inside the description box
   * @author Shivam Mishra
   */
  useEffect(() => {
    const handleMouseMove = (event) => checkIfMouseInside(event);
    // Add mouse move event listener
    document.addEventListener("mousemove", handleMouseMove);
    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <>
      {(hashPopupVisible || visible) && (
        <Popup
          anchor={selectedLink}
          show={hashPopupVisible || visible}
          id='editor-update-popup'
          ref={ref}
          anchorAlign={HyperLinkPopupPosition.anchorAlign}
          popupAlign={HyperLinkPopupPosition.popupAlign}
          margin={{ vertical: number.ZERO }}>
          <div id='editor-popup-handle-hyperlink' ref={editorPopupRef} className='container pt-2 pb-2'>
            <HyperLinkPopup
              url={url}
              selectedLink={selectedLink}
              setVisible={setVisible}
              visible={visible}
              editorRef={editorRef}
              taskInfo={taskInfo}
              editableRef={editableRef}
              setHashPopupVisible={setHashPopupVisible}
              hashPopupVisible={hashPopupVisible}
            />
          </div>
        </Popup>
      )}
      {/* @ => KendoPopup */}
      <Popup
        show={showInputPopup || showEditChip}
        anchor={showInputPopup ? smartChipTarget : editChip}
        collision={{
          horizontal: "fit",
          vertical: "flip",
        }}
        offset={popupPosition}
        anchorAlign={anchorAlign}
        popupAlign={popupAlign}
        popupClass={` smart-chip-popup ${!smartChipTarget && showInputPopup ? "d-none" : ""} ${showEditChip ? "has-date-picker" : ""}`}>
        {assigneeList && (
          <div ref={editorPopupRef} id={showInputPopup ? "editor-popup-smart-chip" : "editor-popup-edit-chip"}>
            {showInputPopup ? (
              <SmartChip
                updateNonEditable={updateNonEditable}
                assigneeList={assigneeList}
                setAssigneeList={setAssigneeList}
                setInputPopup={setInputPopup}
                acknowledgeMailEditor={acknowledgeMailEditor}
                taskKey={taskKey}
                hidePopup={hidePopup}
                editorRef={editorRef}
                insertTaskChip={insertTaskChip}
                workflowDescriptionProjectId={workflowDescriptionProjectId}
              />
            ) : (
              <UpdateChip
                smartChipId={smartChipId}
                setShowEditChip={setShowEditChip}
                assigneeList={assigneeList}
                setAssigneeList={setAssigneeList}
                editorRef={editorRef}
              />
            )}
          </div>
        )}
      </Popup>
      {
        <div className='add-button-wrapper'>
          {showTaskSearchPopup && !isTaskIdTabActive && (
            <OverallSearchPopup // Task Search Popup
              anchor={overallSearchRef}
              setShowPopup={setTaskSearchPopup}
              entityName={editorEntity.TASK_DESC}
              insertTaskChip={insertTaskChip}
            />
          )}
        </div>
      }
      <Popup
        anchor={document.getElementById(editIframeId)}
        show={editIframeId && !showEditIframe && isInside}
        id='editor-update-popup'
        ref={ref}
        anchorAlign={iframePopupPosition.anchorAlign}
        popupAlign={iframePopupPosition.popupAlign}>
        <div id='editor-iframe-popup' ref={editorPopupRef} className='container pt-2 pb-2'>
          <UpdateIFrame editorRef={editorRef} />
        </div>
      </Popup>

      {popupAnchor && (
        <Popup anchor={popupAnchor} show={popupVisible}>
          <BookmarkPopup setPopupVisible={setPopupVisible} deleteBookmark={deleteBookmark} target={popupAnchor} />
        </Popup>
      )}
    </>
  );
};

export default EditorPopup;
